<template>
  <v-container
    fluid
    class="create-live-page responsive-height"
    :class="{ 'create-live-page--dark': $vuetify.theme.dark }"
  >
    <div v-if="step === 1" class="creation-layout">
      <main class="creation-main">
        <section class="steps-panel">
          <div class="step-item step-item--active">
            <span class="step-number">1</span>
            <div><strong>Account Info</strong><small>Set up your trading account</small></div>
          </div>
          <span class="step-line"></span>
          <div class="step-item">
            <span class="step-number">2</span>
            <div><strong>Password</strong><small>Create secure password</small></div>
          </div>
        </section>

        <section class="account-form-card">
          <header class="account-form-heading">
            <span class="heading-icon"><v-icon size="37">mdi-account-plus-outline</v-icon></span>
            <div><h2>Account Information</h2><p>Choose your preferred trading conditions</p></div>
          </header>

          <form class="account-info-form" @submit.prevent="handleNext">
            <div class="account-field-card">
              <label>Account Type</label>
              <p>Select the type of account you want to open</p>
              <div class="field-control custom-dropdown" :class="{ 'custom-dropdown--open': openDropdown === 'account' }">
                <v-icon size="21">mdi-briefcase-outline</v-icon>
                <button
                  type="button"
                  class="dropdown-trigger"
                  aria-haspopup="listbox"
                  :aria-expanded="openDropdown === 'account' ? 'true' : 'false'"
                  @click="toggleDropdown('account')"
                >
                  <span :class="{ 'dropdown-placeholder': !selectedAccountId }">
                    {{ selectedAccountName || 'Select Account Type' }}
                  </span>
                </button>
                <v-icon size="20">mdi-chevron-down</v-icon>
                <ul v-show="openDropdown === 'account'" class="dropdown-menu" role="listbox">
                  <li v-if="!accountTypes.length" class="dropdown-option dropdown-option--empty">No account types available</li>
                  <template v-else>
                    <li
                      v-for="account in accountTypes"
                      :key="account.id"
                      class="dropdown-option"
                      :class="{ 'dropdown-option--selected': String(selectedAccountId) === String(account.id) }"
                      role="option"
                      @click="selectAccount(account)"
                    >
                      {{ account.account_type_display }}
                    </li>
                  </template>
                </ul>
              </div>
              <span v-if="errors.accountType" class="form-error">{{ errors.accountType }}</span>
            </div>

            <div class="account-field-card">
              <label>Leverage</label>
              <p>Choose your leverage</p>
              <div class="field-control custom-dropdown" :class="{ 'custom-dropdown--open': openDropdown === 'leverage' }">
                <v-icon size="22">mdi-chart-bar</v-icon>
                <button
                  type="button"
                  class="dropdown-trigger"
                  aria-haspopup="listbox"
                  :aria-expanded="openDropdown === 'leverage' ? 'true' : 'false'"
                  @click="toggleDropdown('leverage')"
                >
                  <span :class="{ 'dropdown-placeholder': !selectedLeverage }">
                    {{ selectedLeverage ? `1:${selectedLeverage}` : 'Select Leverage' }}
                  </span>
                </button>
                <v-icon size="20">mdi-chevron-down</v-icon>
                <ul v-show="openDropdown === 'leverage'" class="dropdown-menu" role="listbox">
                  <li v-if="!formattedLeverages.length" class="dropdown-option dropdown-option--empty">
                    {{ selectedAccountId ? 'No leverage options available' : 'Select an account type first' }}
                  </li>
                  <template v-else>
                    <li
                      v-for="item in formattedLeverages"
                      :key="item.id"
                      class="dropdown-option"
                      :class="{ 'dropdown-option--selected': String(selectedLeverage) === String(item.leverage) }"
                      role="option"
                      @click="selectLeverage(item)"
                    >
                      {{ item.displayText }}
                    </li>
                  </template>
                </ul>
              </div>
              <span v-if="errors.leverage" class="form-error">{{ errors.leverage }}</span>
            </div>

            <div class="account-field-card">
              <label>Currency</label>
              <p>Select your account base currency</p>
              <div class="field-control field-control--readonly">
                <v-icon size="22">mdi-currency-usd-circle-outline</v-icon>
                <span>{{ currency || 'USD' }}</span>
                <v-icon size="20">mdi-chevron-down</v-icon>
              </div>
            </div>

            <div class="next-step-bar">
              <span class="info-icon"><v-icon size="23">mdi-information-outline</v-icon></span>
              <p>You can create multiple trading accounts with different account types and leverage settings.</p>
              <button type="submit">Next Step <v-icon size="21">mdi-arrow-right</v-icon></button>
            </div>
          </form>
        </section>
      </main>

      <aside class="account-guide">
        <header class="guide-heading">
          <span><v-icon size="35">mdi-chart-bar</v-icon></span>
          <div><h2>Choose the Right Account for You</h2><p>Trade with the best conditions and grow your potential</p></div>
        </header>

        <article
          v-for="account in accountRecommendations"
          :key="account.name"
          class="account-recommendation"
          :class="`account-recommendation--${account.variant}`"
        >
          <div class="recommendation-top">
            <span class="recommendation-icon"><v-icon size="32">{{ account.icon }}</v-icon></span>
            <div>
              <div class="recommendation-name">
                <h3>{{ account.name }}</h3>
                <span v-if="account.badge">{{ account.badge }}</span>
              </div>
              <p>{{ account.description }}</p>
            </div>
          </div>
          <div class="recommendation-stats">
            <div><span>Spread From</span><strong>{{ account.spread }}</strong></div>
            <div><span>Commission</span><strong>{{ account.commission }}</strong></div>
            <div><span>Min Deposit</span><strong>{{ account.deposit }}</strong></div>
          </div>
        </article>

        <section class="why-trade-card">
          <h3>Why Trade With Us?</h3>
          <div class="benefit-grid">
            <div v-for="benefit in benefits" :key="benefit.title" class="benefit">
              <span><v-icon size="27">{{ benefit.icon }}</v-icon></span>
              <div><strong>{{ benefit.title }}</strong><small>{{ benefit.text }}</small></div>
            </div>
          </div>
        </section>
      </aside>
    </div>

    <div v-else class="password-step">
      <section class="steps-panel steps-panel--password">
        <div class="step-item step-item--complete">
          <span class="step-number"><v-icon size="22">mdi-check</v-icon></span>
          <div><strong>Account Info</strong><small>Account information completed</small></div>
        </div>
        <span class="step-line"></span>
        <div class="step-item step-item--active">
          <span class="step-number">2</span>
          <div><strong>Password</strong><small>Set your account password</small></div>
        </div>
      </section>

      <section class="password-card">
        <header class="password-heading">
          <span class="heading-icon heading-icon--password"><v-icon size="31">mdi-safe-square-outline</v-icon></span>
          <div><h2>Set a strong password for your account</h2><p>Ensure your account is protected with a secure password.</p></div>
          <div class="security-illustration" aria-hidden="true">
            <span class="shield"><v-icon size="54">mdi-shield-lock-outline</v-icon></span>
            <span class="password-dots">••••••<em>••</em></span>
            <span class="verified"><v-icon size="21">mdi-check</v-icon></span>
          </div>
        </header>

        <div class="password-content">
          <form class="password-form" @submit.prevent="handleSubmit">
            <label class="password-field">
              <span>Password</span>
              <div class="password-control">
                <input v-model="password" :type="showPassword ? 'text' : 'password'" autocomplete="new-password" />
                <button type="button" :aria-label="showPassword ? 'Hide password' : 'Show password'" @click="showPassword = !showPassword">
                  <v-icon size="22">{{ showPassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon>
                </button>
              </div>
            </label>

            <p class="requirements-label">Password must contain</p>
            <div class="password-requirements">
              <span v-for="requirement in passwordRequirements" :key="requirement.label" :class="{ valid: requirement.valid }">
                <v-icon size="19">{{ requirement.valid ? 'mdi-check-circle-outline' : 'mdi-circle-outline' }}</v-icon>
                {{ requirement.label }}
              </span>
            </div>

            <div class="strength-card" :class="`strength-card--${strengthLevel.toLowerCase()}`">
              <v-icon size="29">mdi-shield-check-outline</v-icon>
              <div>
                <p>Password strength: <strong>{{ strengthLevel }}</strong></p>
                <div class="strength-bars">
                  <span v-for="index in 5" :key="index" :class="{ active: index <= strengthScore }"></span>
                </div>
              </div>
            </div>

            <label class="password-field confirm-field">
              <span>Confirm Password</span>
              <div class="password-control">
                <input v-model="confirmPassword" :type="showConfirmPassword ? 'text' : 'password'" autocomplete="new-password" />
                <button type="button" :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'" @click="showConfirmPassword = !showConfirmPassword">
                  <v-icon size="22">{{ showConfirmPassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon>
                </button>
              </div>
              <small v-if="confirmPassword" :class="passwordsMatch ? 'match-text' : 'mismatch-text'">
                <v-icon size="16">{{ passwordsMatch ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}</v-icon>
                {{ passwordsMatch ? 'Passwords match' : 'Passwords do not match' }}
              </small>
            </label>

            <span v-if="errors.password" class="form-error password-error">{{ errors.password }}</span>

            <div class="password-actions">
              <button type="button" class="previous-button" @click="step = 1">
                <v-icon size="20">mdi-arrow-left</v-icon> Previous Step
              </button>
              <button type="submit" class="create-button" :disabled="!allConditionsMet || loading">
                <v-progress-circular v-if="loading" indeterminate size="18" width="2" />
                <span>{{ loading ? 'Creating...' : 'Create Account' }}</span>
                <v-icon v-if="!loading" size="20">mdi-arrow-right</v-icon>
              </button>
            </div>
          </form>

          <aside class="password-tips-card">
            <h3><v-icon size="23">mdi-lightbulb-outline</v-icon> Password Tips</h3>
            <ul>
              <li v-for="tip in passwordTips" :key="tip.text">
                <v-icon size="23">{{ tip.icon }}</v-icon><span>{{ tip.text }}</span>
              </li>
            </ul>
            <div class="tips-divider"></div>
            <h4>Need help?</h4>
            <p>If you have any issues creating your account, our support team is here to help you.</p>
            <router-link to="/support"><v-icon size="21">mdi-headset</v-icon> Contact Support <v-icon size="19">mdi-arrow-right</v-icon></router-link>
          </aside>
        </div>

        <footer class="password-terms">
          By creating an account, you agree to our
          <router-link to="/terms-and-condition">Terms &amp; Conditions</router-link>
          and <a href="#">Privacy Policy</a>.
        </footer>
      </section>
    </div>
  </v-container>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { TRADING__ACTIONS } from "@/store/action-types";

export default {
  name: "CreateLiveAccount",
  data() {
    return {
      step: 1,
      loading: false,
      selectedAccountId: null,
      selectedLeverage: null,
      openDropdown: "",
      currency: "USD",
      currencySymbol: "$",
      password: "",
      confirmPassword: "",
      showPassword: false,
      showConfirmPassword: false,
      errors: { accountType: "", leverage: "", password: "" },
      accountRecommendations: [
        { name: "Standard Account", badge: "Popular", description: "Ideal for most traders. Competitive spreads and no commission.", spread: "1.0 pips", commission: "$0", deposit: "$100", icon: "mdi-star-outline", variant: "standard" },
        { name: "ECN Account", description: "Raw spreads with low commission. Best for professional traders.", spread: "0.0 pips", commission: "$3.5 / lot", deposit: "$500", icon: "mdi-diamond-stone", variant: "ecn" },
        { name: "VIP Account", description: "Exclusive conditions for high volume traders.", spread: "0.0 pips", commission: "$2.0 / lot", deposit: "$5,000", icon: "mdi-database-outline", variant: "vip" },
      ],
      benefits: [
        { title: "Ultra Fast Execution", text: "Trade with low latency", icon: "mdi-lightning-bolt-outline" },
        { title: "Competitive Spreads", text: "Best market rates", icon: "mdi-database-outline" },
        { title: "Secure & Regulated", text: "Your funds are safe", icon: "mdi-shield-outline" },
        { title: "24/7 Support", text: "We're always here", icon: "mdi-headset" },
      ],
      passwordTips: [
        { text: "Use a combination of letters, numbers & symbols", icon: "mdi-vector-polyline" },
        { text: "Avoid using personal information", icon: "mdi-lock-outline" },
        { text: "Use at least 8 characters", icon: "mdi-card-text-outline" },
        { text: "Make it unique and memorable", icon: "mdi-shield-key-outline" },
      ],
    };
  },
  computed: {
    ...mapState("trading", ["accountTypes", "leverage_account_type"]),
    formattedLeverages() {
      return (this.leverage_account_type || []).map((item) => ({
        id: item.id,
        leverage: item.leverage,
        displayText: `1:${item.leverage}`,
      }));
    },
    selectedAccountName() {
      const account = this.accountTypes.find(
        (item) => String(item.id) === String(this.selectedAccountId)
      );
      return account?.account_type_display || "";
    },
    hasMinimumLength() { return this.password.length >= 8; },
    hasLowerCase() { return /[a-z]/.test(this.password); },
    hasUpperCase() { return /[A-Z]/.test(this.password); },
    hasDigit() { return /\d/.test(this.password); },
    hasSpecialCharacter() { return /[@$!%*#?&^_-]/.test(this.password); },
    passwordsMatch() { return Boolean(this.confirmPassword) && this.password === this.confirmPassword; },
    passwordRequirements() {
      return [
        { label: "8+ Characters", valid: this.hasMinimumLength },
        { label: "1 Lowercase", valid: this.hasLowerCase },
        { label: "1 Uppercase", valid: this.hasUpperCase },
        { label: "1 Number", valid: this.hasDigit },
        { label: "1 Symbol", valid: this.hasSpecialCharacter },
      ];
    },
    strengthScore() {
      return this.passwordRequirements.filter((item) => item.valid).length;
    },
    strengthLevel() {
      if (this.strengthScore >= 5) return "Strong";
      if (this.strengthScore >= 3) return "Medium";
      return "Weak";
    },
    allConditionsMet() {
      return this.strengthScore === 5 && this.passwordsMatch;
    },
  },
  watch: {
    selectedAccountId() { this.errors.accountType = ""; },
    selectedLeverage() { this.errors.leverage = ""; },
    confirmPassword() { this.errors.password = ""; },
  },
  methods: {
    ...mapActions("trading", [
      TRADING__ACTIONS.ACCOUNTTYPES,
      TRADING__ACTIONS.LEVERAGES,
      TRADING__ACTIONS.OPENTRADINGACCOUNT,
      TRADING__ACTIONS.LEVERAGEACCOUNTTYPE,
    ]),
    async handleAccount(selectedId) {
      const account = this.accountTypes.find((item) => String(item.id) === String(selectedId));
      if (account) {
        this.currency = account.currencies?.currency_name || "USD";
        this.currencySymbol = account.currencies?.symbol || "$";
      }
      this.selectedLeverage = null;
      if (selectedId) await this[TRADING__ACTIONS.LEVERAGEACCOUNTTYPE](selectedId);
    },
    toggleDropdown(name) {
      this.openDropdown = this.openDropdown === name ? "" : name;
    },
    async selectAccount(account) {
      this.selectedAccountId = account.id;
      this.openDropdown = "";
      await this.handleAccount(account.id);
    },
    selectLeverage(item) {
      this.selectedLeverage = item.leverage;
      this.openDropdown = "";
    },
    handleNext() {
      this.errors.accountType = this.selectedAccountId ? "" : "Select at least one account type";
      this.errors.leverage = this.selectedLeverage ? "" : "Select at least one leverage";
      if (!this.errors.accountType && !this.errors.leverage) {
        this.step = 2;
        this.$nextTick(() => this.scrollToTop());
      }
    },
    scrollToTop() {
      const page = this.$el;
      if (page && typeof page.scrollTo === "function") page.scrollTo({ top: 0, behavior: "smooth" });
    },
    async handleSubmit() {
      if (!this.allConditionsMet) {
        this.errors.password = "Please meet all password requirements";
        return;
      }
      this.loading = true;
      try {
        const response = await this[TRADING__ACTIONS.OPENTRADINGACCOUNT]({
          trading_account_type_id: this.selectedAccountId,
          leverage: this.selectedLeverage,
          password: this.password,
        });
        if (response?.success) this.resetForm();
      } catch (error) {
        console.error("Live account creation error:", error);
        this.errors.password = "An error occurred. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.step = 1;
      this.selectedAccountId = null;
      this.selectedLeverage = null;
      this.currency = "USD";
      this.currencySymbol = "$";
      this.password = "";
      this.confirmPassword = "";
      this.showPassword = false;
      this.showConfirmPassword = false;
      this.openDropdown = "";
      this.errors = { accountType: "", leverage: "", password: "" };
      this.scrollToTop();
    },
  },
  async mounted() {
    await Promise.all([
      this[TRADING__ACTIONS.ACCOUNTTYPES](),
      this[TRADING__ACTIONS.LEVERAGES](),
    ]);
  },
};
</script>

<style scoped>
.create-live-page {
  --page: #f7f8fa;
  --panel: #ffffff;
  --panel-soft: #f4f5f7;
  --field: #ffffff;
  --border: #dfe2e6;
  --border-soft: #eaebed;
  --text: #171a1f;
  --muted: #50565f;
  --subtle: #747b84;
  --blue: #d99a00;
  --blue-soft: #fff7df;
  min-height: calc(100vh - 64px);
  padding: 14px 12px 24px;
  color: var(--text);
  background: var(--page);
  font-family: "Inter", sans-serif;
  max-width: none;
  margin: 0;
  width: 100%;
}

.create-live-page--dark {
  --page: #030b13;
  --panel: #07111b;
  --panel-soft: #0b1621;
  --field: #08131e;
  --border: #293026;
  --border-soft: #222a22;
  --text: #f4f5f7;
  --muted: #aeb6c0;
  --subtle: #858e98;
  --blue: #ffc400;
  --blue-soft: rgba(255, 196, 0, 0.1);
}

.creation-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(0, 0.9fr);
  gap: 10px;
  max-width: 100%;
  margin: 0;
}

.creation-main { min-width: 0; width: 100%; }

.steps-panel {
  display: flex;
  height: 84px;
  padding: 0 24px;
  align-items: center;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 8px;
}

.step-item {
  display: flex;
  min-width: 180px;
  align-items: center;
  gap: 14px;
}

.step-number {
  display: grid;
  width: 43px;
  height: 43px;
  flex: 0 0 43px;
  place-items: center;
  color: #26334f;
  background: #dce2ea;
  border-radius: 50%;
  font-size: 16px;
  font-weight: 600;
}

.create-live-page--dark .step-number { color: #fff; background: #53627b; }
.step-item--active .step-number,
.step-item--complete .step-number { color: #17130a; background: linear-gradient(145deg, #ffd43b, #ffc400); }
.step-item--complete .step-number .v-icon { color: #17130a; }

.step-item div { display: flex; flex-direction: column; gap: 5px; }
.step-item strong { color: var(--text); font-size: 13px; }
.step-item small { color: var(--muted); font-size: 11px; }

.step-line {
  height: 2px;
  margin: 0 14px;
  flex: 1;
  background: linear-gradient(90deg, var(--blue) 0 92%, var(--border) 92%);
}

.account-form-card {
  min-height: auto;
  margin-top: 13px;
  padding: 15px;
  width: 100%;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 9px;
}

.account-form-heading,
.guide-heading,
.password-heading {
  display: flex;
  align-items: center;
  gap: 14px;
}

.heading-icon,
.guide-heading > span {
  display: grid;
  width: 54px;
  height: 54px;
  flex: 0 0 54px;
  place-items: center;
  color: var(--blue);
  background: var(--blue-soft);
  border-radius: 17px;
}

.heading-icon .v-icon,
.guide-heading .v-icon { color: var(--blue); }

.account-form-heading h2,
.password-heading h2 { margin: 0 0 4px; color: var(--text); font-size: 18px; font-weight: 600; }
.account-form-heading p,
.password-heading p,
.guide-heading p { margin: 0; color: var(--muted); font-size: 12px; }

.account-info-form { margin-top: 20px; }

.account-field-card {
  min-height: 128px;
  padding: 20px 18px 16px;
  border: 1px solid var(--border);
  border-radius: 9px;
}

.account-field-card + .account-field-card { margin-top: 6px; }
.account-field-card label { color: var(--text); font-size: 13px; font-weight: 600; }
.account-field-card > p { margin: 5px 0 10px; color: var(--muted); font-size: 11px; }

.field-control {
  position: relative;
  display: flex;
  width: 100%;
  height: 44px;
  padding-left: 14px;
  align-items: center;
  gap: 10px;
  color: var(--text);
  background: var(--field);
  border: 1px solid var(--border);
  border-radius: 6px;
}

.field-control > .v-icon:first-child { color: var(--blue); }
.field-control > .v-icon:last-child { position: absolute; right: 12px; color: var(--muted); pointer-events: none; }

.field-control select {
  width: 100%;
  height: 100%;
  padding-right: 38px;
  color: var(--text);
  background: transparent;
  border: 0;
  outline: 0;
  appearance: none;
  font-family: inherit;
  font-size: 12px;
}

.custom-dropdown--open {
  z-index: 20;
  border-color: var(--blue);
  box-shadow: 0 0 0 1px rgba(217, 154, 0, 0.1);
}

.dropdown-trigger {
  width: 100%;
  height: 100%;
  padding: 0 42px 0 0;
  color: var(--text);
  background: transparent;
  border: 0;
  outline: 0;
  text-align: left;
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
}

.dropdown-placeholder {
  color: var(--muted);
}

.dropdown-menu {
  position: absolute;
  z-index: 30;
  top: calc(100% + 2px);
  right: -1px;
  left: -1px;
  max-height: 235px;
  margin: 0;
  padding: 6px;
  overflow-y: auto;
  list-style: none;
  color: var(--text);
  background: var(--field);
  border: 1px solid var(--border);
  border-radius: 6px;
  box-shadow: 0 12px 28px rgba(2, 11, 25, 0.24);
}

.create-live-page--dark .dropdown-menu {
  background: #0b1621;
  border-color: #343b32;
}

.dropdown-option {
  display: flex;
  min-height: 38px;
  padding: 8px 13px;
  align-items: center;
  color: var(--text);
  background: transparent;
  border-radius: 5px;
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
}

.dropdown-option:hover,
.dropdown-option--selected {
  color: var(--blue);
  background: var(--blue-soft);
}

.create-live-page--dark .dropdown-option:hover,
.create-live-page--dark .dropdown-option--selected {
  color: #ffffff;
  background: #5a470c;
}

.dropdown-option--empty {
  color: var(--muted);
  cursor: default;
}

.dropdown-option--empty:hover {
  color: var(--muted);
  background: transparent;
}

.field-control--readonly > span { font-size: 12px; }
.form-error { display: block; margin-top: 6px; color: #ff4055; font-size: 11px; }

.next-step-bar {
  display: flex;
  min-height: 66px;
  margin-top: 13px;
  padding: 10px 12px 10px 18px;
  align-items: center;
  gap: 12px;
  background: rgba(217, 154, 0, 0.035);
  border: 1px solid rgba(217, 154, 0, 0.2);
  border-radius: 8px;
}

.info-icon { display: grid; width: 35px; height: 35px; flex: 0 0 35px; place-items: center; background: var(--blue-soft); border-radius: 50%; }
.info-icon .v-icon { color: var(--blue); }
.next-step-bar p { max-width: 280px; margin: 0; color: var(--muted); font-size: 10px; line-height: 16px; }
.next-step-bar button,
.create-button {
  display: flex;
  height: 40px;
  margin-left: auto;
  padding: 0 28px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #17130a;
  background: linear-gradient(180deg, #ffd43b, #ffc400);
  border: 0;
  border-radius: 6px;
  box-shadow: 0 6px 14px rgba(217, 154, 0, 0.2);
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.next-step-bar button .v-icon,
.create-button .v-icon { color: #17130a; }

.account-guide {
  min-height: auto;
  padding: 12px 10px;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 9px;
  width: auto;
}

.guide-heading > span { width: 46px; height: 46px; flex-basis: 46px; border-radius: 15px; }
.guide-heading h2 { margin: 0 0 5px; color: var(--text); font-size: 15px; font-weight: 600; }

.account-recommendation {
  min-height: 132px;
  margin-top: 8px;
  padding: 10px 10px 8px;
  border: 1px solid;
  border-radius: 9px;
}
.account-recommendation--standard { background: rgba(0, 181, 103, 0.025); border-color: rgba(0, 181, 103, 0.28); }
.account-recommendation--ecn { background: rgba(217, 154, 0, 0.035); border-color: rgba(217, 154, 0, 0.3); }
.account-recommendation--vip { background: rgba(230, 157, 0, 0.025); border-color: rgba(230, 157, 0, 0.3); }
.create-live-page--dark .account-recommendation--standard { background: linear-gradient(135deg, rgba(0, 96, 78, .25), rgba(6, 38, 47, .16)); }
.create-live-page--dark .account-recommendation--ecn { background: linear-gradient(135deg, rgba(255, 196, 0, .12), rgba(7, 17, 27, .2)); }
.create-live-page--dark .account-recommendation--vip { background: linear-gradient(135deg, rgba(126, 83, 13, .2), rgba(31, 31, 43, .13)); }

.recommendation-top { display: flex; gap: 12px; }
.recommendation-icon { display: grid; width: 46px; height: 46px; flex: 0 0 46px; place-items: center; border-radius: 14px; }
.account-recommendation--standard .recommendation-icon { background: rgba(0, 190, 105, .19); }
.account-recommendation--standard .recommendation-icon .v-icon { color: #00ac63; }
.account-recommendation--ecn .recommendation-icon { background: linear-gradient(145deg, #ffd43b, #d99a00); }
.account-recommendation--ecn .recommendation-icon .v-icon { color: #17130a; }
.account-recommendation--vip .recommendation-icon { background: rgba(255, 183, 35, .22); }
.account-recommendation--vip .recommendation-icon .v-icon { color: #f0a900; }
.recommendation-top > div { min-width: 0; flex: 1; }
.recommendation-name { display: flex; align-items: center; gap: 10px; }
.recommendation-name h3 { margin: 2px 0 0; color: var(--text); font-size: 13px; }
.recommendation-name span { padding: 3px 10px; color: #009951; background: rgba(0, 198, 105, .12); border-radius: 14px; font-size: 9px; }
.recommendation-top p { margin: 6px 0 0; color: var(--muted); font-size: 10px; line-height: 16px; }

.recommendation-stats { display: grid; grid-template-columns: 1fr; row-gap: 8px; margin-top: 10px; padding-top: 10px; border-top: 1px solid var(--border-soft); }
.recommendation-stats div { display: flex; padding: 6px 0; flex-direction: row; justify-content: space-between; gap: 10px; border-right: 0; border-bottom: 1px solid var(--border-soft); }
.recommendation-stats div:last-child { border-bottom: 0; }
.recommendation-stats span { color: var(--muted); font-size: 10px; }
.recommendation-stats strong { color: var(--text); font-size: 11px; }

.why-trade-card { min-height: 140px; margin-top: 16px; padding: 12px; border: 1px solid var(--border); border-radius: 8px; }
.why-trade-card h3 { margin: 0 0 10px; color: var(--text); font-size: 13px; }
.benefit-grid { display: grid; grid-template-columns: 1fr; gap: 10px; }
.benefit { display: flex; align-items: center; gap: 10px; }
.benefit > span { display: grid; width: 34px; height: 34px; flex: 0 0 34px; place-items: center; background: var(--blue-soft); border-radius: 50%; }
.benefit .v-icon { color: var(--blue); }
.benefit div { display: flex; flex-direction: column; gap: 4px; }
.benefit strong { color: var(--text); font-size: 9px; }
.benefit small { color: var(--muted); font-size: 8px; }

.password-step { max-width: none; margin: 0; }
.steps-panel--password { width: 100%; height: auto; padding: 0 18px; background: transparent; border: 0; }
.steps-panel--password .step-line { max-width: 65px; background: var(--blue); }

.password-card { min-height: auto; width: min(100%, 920px); margin: 0; background: var(--panel); border: 1px solid var(--border); border-radius: 9px; overflow: hidden; }
.password-heading { position: relative; height: 139px; padding: 0 31px; border-bottom: 1px solid var(--border); overflow: hidden; }
.heading-icon--password { width: 65px; height: 65px; flex-basis: 65px; border-radius: 50%; }
.security-illustration { position: absolute; right: 55px; display: flex; align-items: center; }
.shield { display: grid; width: 82px; height: 92px; place-items: center; color: #ffc400; background: linear-gradient(145deg, rgba(255, 196, 0, .22), rgba(217, 154, 0, .1)); border: 2px solid #d99a00; border-radius: 48% 48% 55% 55%; }
.shield .v-icon { color: #ffc400; }
.password-dots { width: 130px; height: 39px; margin-left: -8px; color: #ffc400; border: 1px solid #d99a00; border-radius: 8px; text-align: center; font-style: normal; font-size: 22px; letter-spacing: 2px; line-height: 35px; }
.password-dots em { color: #8a6500; font-style: normal; }
.verified { display: grid; width: 35px; height: 35px; margin-left: -6px; place-items: center; background: #d99a00; border-radius: 50%; }
.verified .v-icon { color: #17130a; }

.password-content { display: grid; grid-template-columns: minmax(420px, 1fr) 260px; gap: 24px; padding: 22px 18px 28px; }
.password-form { min-width: 0; }
.password-field { display: flex; flex-direction: column; gap: 9px; color: var(--text); font-size: 12px; font-weight: 600; }
.password-control { position: relative; display: flex; height: 52px; align-items: center; background: var(--field); border: 1px solid var(--border); border-radius: 6px; }
.password-control input { width: 100%; height: 100%; padding: 0 54px 0 17px; color: var(--text); background: transparent; border: 0; outline: 0; font-family: inherit; font-size: 15px; }
.password-control input:-webkit-autofill,
.password-control input:-webkit-autofill:hover,
.password-control input:-webkit-autofill:focus,
.password-control input:-webkit-autofill:active {
  box-shadow: inset 0 0 0 1000px var(--field) !important;
  -webkit-text-fill-color: var(--text) !important;
  background-color: transparent !important;
}
.password-control button { position: absolute; right: 12px; display: grid; width: 34px; height: 34px; place-items: center; background: transparent; border: 0; cursor: pointer; }
.password-control button .v-icon { color: var(--muted); }
.requirements-label { margin: 25px 0 13px; color: var(--text); font-size: 12px; font-weight: 600; }
.password-requirements { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
.password-requirements > span { display: flex; min-height: 47px; padding: 0 12px; align-items: center; justify-content: center; gap: 7px; color: var(--muted); border: 1px solid var(--border); border-radius: 6px; white-space: nowrap; font-size: 11px; }
.password-requirements .v-icon { color: var(--subtle); }
.password-requirements .valid .v-icon,
.password-requirements .valid { color: #1ec774; }

.strength-card { display: flex; height: 83px; margin-top: 25px; padding: 17px 20px; align-items: center; gap: 17px; background: rgba(20, 189, 107, .04); border: 1px solid rgba(20, 189, 107, .25); border-radius: 6px; }
.strength-card > .v-icon { color: #20c576; }
.strength-card > div { flex: 1; }
.strength-card p { margin: 0 0 10px; color: var(--muted); font-size: 12px; }
.strength-card strong { color: #20c576; }
.strength-bars { display: grid; grid-template-columns: repeat(5, 1fr); gap: 5px; }
.strength-bars span { height: 6px; background: var(--border); border-radius: 5px; }
.strength-bars span.active { background: #25c97a; }
.strength-card--weak strong { color: #ff4c5f; }
.strength-card--weak .strength-bars span.active { background: #ff4c5f; }
.strength-card--medium strong { color: #f0a900; }
.strength-card--medium .strength-bars span.active { background: #f0a900; }

.confirm-field { margin-top: 32px; }
.confirm-field small { display: flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 400; }
.confirm-field small .v-icon { color: currentColor; }
.match-text { color: #1ec774; }
.mismatch-text { color: #ff4c5f; }
.password-error { margin-top: 9px; }
.password-actions { display: flex; margin-top: 37px; gap: 22px; }
.previous-button { display: flex; width: 177px; height: 50px; align-items: center; justify-content: center; gap: 10px; color: var(--blue); background: transparent; border: 1px solid var(--blue); border-radius: 5px; font-family: inherit; font-size: 12px; cursor: pointer; }
.previous-button .v-icon { color: var(--blue); }
.create-button { width: 227px; height: 50px; margin-left: 0; padding: 0 25px; }
.create-button:disabled { opacity: .48; cursor: not-allowed; }

.password-tips-card { min-height: auto; padding: 18px; border: 1px solid var(--border); border-radius: 8px; }
.password-tips-card h3 { display: flex; margin: 0 0 18px; align-items: center; gap: 9px; color: var(--text); font-size: 14px; }
.password-tips-card h3 .v-icon,
.password-tips-card li .v-icon { color: var(--blue); }
.password-tips-card ul { display: flex; margin: 0; padding: 0; flex-direction: column; gap: 18px; list-style: none; }
.password-tips-card li { display: flex; align-items: flex-start; gap: 12px; color: var(--muted); font-size: 10px; line-height: 16px; }
.password-tips-card li .v-icon { flex: 0 0 20px; }
.tips-divider { margin: 24px 0 20px; border-top: 1px solid var(--border); }
.password-tips-card h4 { margin: 0 0 12px; color: var(--text); font-size: 11px; }
.password-tips-card > p { margin: 0; color: var(--muted); font-size: 10px; line-height: 16px; }
.password-tips-card > a { display: flex; height: 42px; margin-top: 18px; padding: 0 14px; align-items: center; gap: 10px; color: var(--blue); border: 1px solid var(--blue); border-radius: 5px; text-decoration: none; font-size: 11px; font-weight: 600; }
.password-tips-card > a .v-icon { color: var(--blue); }
.password-tips-card > a .v-icon:last-child { margin-left: auto; }

.password-terms { height: 80px; padding-top: 31px; color: var(--muted); border-top: 1px solid var(--border); text-align: center; font-size: 11px; }
.password-terms a { margin: 0 4px; color: var(--blue); text-decoration: none; }

@media (max-width: 1250px) {
  .creation-layout { grid-template-columns: minmax(540px, 1.45fr) minmax(360px, 1fr); gap: 18px; }
  .steps-panel { padding: 0 35px; }
  .account-guide { padding: 21px 18px; }
  .password-content { grid-template-columns: minmax(520px, 1fr) 285px; gap: 28px; }
  .password-requirements { grid-template-columns: repeat(3, 1fr); }
}

@media (max-width: 980px) {
  .create-live-page { padding: 18px; }
  .creation-layout { grid-template-columns: 1fr; }
  .account-guide { min-height: auto; }
  .password-content { grid-template-columns: 1fr; }
  .security-illustration { display: none; }
  .password-tips-card { min-height: auto; }
}

@media (max-width: 650px) {
  .create-live-page { height: calc(100vh - 56px); padding: 12px; }
  .steps-panel { height: auto; min-height: 115px; padding: 16px; }
  .step-item { min-width: 0; }
  .step-item small { display: none; }
  .step-line { margin: 0 7px; }
  .account-form-card { padding: 17px; }
  .account-form-heading h2,
  .password-heading h2 { font-size: 17px; }
  .heading-icon { width: 52px; height: 52px; flex-basis: 52px; }
  .next-step-bar { align-items: flex-start; flex-wrap: wrap; }
  .next-step-bar button { width: 100%; margin-left: 0; }
  .benefit-grid { grid-template-columns: 1fr; }
  .steps-panel--password { width: 100%; }
  .password-heading { height: auto; min-height: 125px; padding: 20px; }
  .password-content { padding: 20px; }
  .password-requirements { grid-template-columns: repeat(2, 1fr); }
  .password-actions { flex-direction: column; }
  .previous-button,
  .create-button { width: 100%; }
}
</style>
